import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { ITextItemLinkModel } from '@fi-shared/fi-text-item-helper/fi-text-item-link';
import { Observable, Subject } from 'rxjs';
import { IDocumentLayoutImageModel } from '@fi-app/document/document-layout/layout-item-image/image-column/image-column.component';
import { FiMediaPanelHandlerBase } from '@fi-app/utilities/insert-media/fi-media-panel-handler-base';
import { takeUntil } from 'rxjs/operators';
import { findClosestElement, isDefined, isEqual } from '@fi-shared/utils';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { FiDisplayAs } from '@fi-shared/types/display-as';
import { LinkToModel } from '@fi-shared/link-to/link-to.model';
import { TextItemMediaClickActionType } from '@fi-shared/types/text-item-media-click-action-type';
import {
    ITextItemMediaModel,
    ITextItemMediaSettingsDialogData,
} from '@fi-shared/fi-text-item-helper/fi-text-item-media';
import { isNullOrUndefined } from 'util';

@Component({
    selector: 'fi-image-media-panel',
    template: `
        @if ((currentSelectedElement$ | async) && isMediaDisplayType() && isTemplateState) {
          <fi-text-item-media-panel-link
            (attachLinkToTextItemMedia)="handleAttachLinkToTextItemMedia($event)"
            (clearLinkToTextItemMedia)="clearLinkToTextItemMedia()"
            [textItemMediaModel]="contentFile"
            [closeMenu$]="closeMenu$.asObservable()"
            >
          </fi-text-item-media-panel-link>
          <fi-text-item-media-panel-size
            [currentItemMediaSizeClass]="currentItemMediaSizeClass"
            (setSizeClass)="setSizeClass($event)"
            [closeMenu$]="closeMenu$.asObservable()"
            >
          </fi-text-item-media-panel-size>
          <fi-text-item-media-panel-alignment
            [currentItemMediaAlignmentClass]="currentItemMediaAlignmentClass"
            (setAlignmentClass)="setAlignmentClass($event)"
            [closeMenu$]="closeMenu$.asObservable()"
            >
          </fi-text-item-media-panel-alignment>
          <fi-text-item-media-panel-settings
            (openSettingsDialog)="openSettingsDialog()"
            [closeMenu$]="closeMenu$.asObservable()"
            >
          </fi-text-item-media-panel-settings>
          <button mat-icon-button class="fi-text-item-media-panel-action" (click)="deleteMediaItem()">
            <mat-icon><span class="fas fa-trash"></span></mat-icon>
          </button>
        }
        `,
    styleUrls: ['./fi-image-media-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-image-media-panel-handler' },
    standalone: false,
})
export class FiImageMediaPanelComponent extends FiMediaPanelHandlerBase implements OnInit {
    @Input() mediaElement: HTMLElement;
    @Input() contentFile: IDocumentLayoutImageModel;
    @Input() openSettingsDialog$: Observable<void> = new Observable<void>();
    @Output() onDeleteImage: EventEmitter<void> = new EventEmitter<void>();
    @Input() isTemplateState: boolean = false;
    closeMenu$: Subject<void> = new Subject<void>();
    currentItemMediaSizeClass: string;
    currentItemMediaAlignmentClass: string;

    constructor(
        injector: Injector,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.setInitialData();
        this.setSubscriptions();
    }

    private setSubscriptions(): void {
        this.openSettingsDialog$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.openSettingsDialog());

        this.fiBrowserEventsService
            .getDocumentEventSubject$('click')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: MouseEvent) => this.handleWindowClick($event.target as HTMLElement));
    }

    private handleWindowClick(target): void {
        if (findClosestElement(target, this.elementRef.nativeElement)) {
            // ignore click on panel
            return;
        }
        const columnNode: Node = target.closest('.fi-document-layout-item-image-column') as Node;
        const vimeoPlayerNode: Node = target.closest('.fi-vimeo-player');
        if (!isNullOrUndefined(vimeoPlayerNode)) {
            target = vimeoPlayerNode;
        }
        if (
            columnNode &&
            columnNode?.contains(this.elementRef.nativeElement) &&
            (target.nodeName.toLowerCase() === 'img' || !isNullOrUndefined(vimeoPlayerNode))
        ) {
            this.currentSelectedElement$.next(target);
            this.lastTextItemMedia = target;
            if (this.isTemplateState) {
                this.toggleItemMediaBorderClass(true);
            }
        } else if (!this.isIgnored(target)) {
            this.currentSelectedElement$.next(null);
            if (this.isTemplateState) {
                this.toggleItemMediaBorderClass(false);
            }
        }
    }

    private setInitialData(): void {
        if (!this.contentFile.MediaSizeClass) {
            this.contentFile.MediaSizeClass = 'fi-text-item-media-item-size-original';
        }
        if (!this.contentFile.AlignmentClass) {
            this.contentFile.AlignmentClass = 'fi-text-item-media-item-alignment-center';
        }
        if (!this.contentFile.DisplayAs) {
            this.contentFile.DisplayAs = FiDisplayAs.Image.Id;
        }
        if (!isDefined(this.contentFile.OnClickActionType)) {
            this.contentFile.OnClickActionType = TextItemMediaClickActionType.EnlargedView;
        }
        this.currentItemMediaSizeClass = this.contentFile.MediaSizeClass;
        this.currentItemMediaAlignmentClass = this.contentFile.AlignmentClass;
        this.panelMinTopOffset = 0;
    }

    isMediaDisplayType(): boolean {
        return this.contentFile && this.contentFile.DisplayAs === FiDisplayAs.Image.Id;
    }

    handleAttachLinkToTextItemMedia(linkToData: ITextItemLinkModel) {
        this.contentFile.linkTo = linkToData;
        this.closeMenu$.next();
    }

    clearLinkToTextItemMedia() {
        this.contentFile.linkTo = new LinkToModel();
    }

    setSizeClass(newClassName: string): void {
        this.currentItemMediaSizeClass = newClassName;
        this.contentFile.MediaSizeClass = newClassName;
        this.updatePanelPosition(this.currentSelectedElement$.value);
    }

    setAlignmentClass(newClassName: string): void {
        this.currentItemMediaAlignmentClass = newClassName;
        this.contentFile.AlignmentClass = newClassName;
        this.updatePanelPosition(this.currentSelectedElement$.value);
    }

    openSettingsDialog() {
        const insertMediaModel = this.getInsertMediaModel();
        const dialogData: ITextItemMediaSettingsDialogData = {
            textItemMediaModel: Object.assign({}, insertMediaModel),
            linkTextValue: this.contentFile.LinkText?.length ? this.contentFile.LinkText : '',
        };
        this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ attachments: true });
        this.openSettingsDialogBase(dialogData)
            .onYesButtonClick(() => this.updateContentFileSettings(insertMediaModel, dialogData))
            .afterClosed(() =>
                this.advancedTextPanelMediaLinkService.setTextEditorActiveButtons({ attachments: false }),
            );
    }

    private updateContentFileSettings(
        insertMediaModel: ITextItemMediaModel,
        dialogData: ITextItemMediaSettingsDialogData,
    ): void {
        if (
            !isEqual(insertMediaModel, dialogData.textItemMediaModel) ||
            dialogData.textItemMediaModel !== this.contentFile.LinkText ||
            this.contentFile.linkTo
        ) {
            this.contentFile.DisplayAs = dialogData.textItemMediaModel.displayAs;
            this.contentFile.OnClickActionType = dialogData.textItemMediaModel.onClickActionType;
            this.contentFile.LinkText = dialogData.linkTextValue;
            delete this.contentFile.linkTo;
            this.currentSelectedElement$.next(null);
        }
    }

    private getInsertMediaModel(): ITextItemMediaModel {
        return {
            displayAs: this.contentFile.DisplayAs,
            displayTitle: this.contentFile.LinkText,
            onClickActionType: this.contentFile.OnClickActionType,
            linkTo: this.contentFile.linkTo,
            file: {
                Name: this.contentFile.PhysicalFileName,
                FullName: this.contentFile.PhysicalFileName + this.contentFile.Extension,
                FileType: this.contentFile.FileType,
                ChangedOn: this.contentFile.ChangedOn,
                StorageType: this.contentFile.StorageType,
                PhysicalFileName: this.contentFile.PhysicalFileName,
                Extension: this.contentFile.Extension,
            },
        };
    }

    deleteMediaItem(): void {
        if (this.isTemplateState) {
            this.toggleItemMediaBorderClass(false);
        }
        this.onDeleteImage.emit();
    }
}
